import { Injectable } from "@angular/core";
import { BehaviorSubject, EMPTY, map, switchMap, tap, of } from "rxjs";
import { IDashboardArgs } from "../types/dashboard-types";
import { TaskDataService } from "../../../services/task-data.service";
import { DashboardTaskDataMapper } from "../utils/dashboard-task-data-mapper";
import { HttpParams } from "@angular/common/http";
import { ScopeOptions } from "../../../../types/resource";

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  constructor(private taskDataService: TaskDataService) {}
  private dashboardCriteriaSelectedSubject =
    new BehaviorSubject<IDashboardArgs>(null);

    dashboardCriteriaSelectedAction$ =
    this.dashboardCriteriaSelectedSubject.asObservable();

  dashboardData$ = this.dashboardCriteriaSelectedSubject.pipe(
    switchMap((params) => {
      let httpParams: HttpParams = new HttpParams();
      if (params != null) {
        let filters: any[] = this.getFiltersFromDashboardArgs(params);

        if (filters.length > 0) {
          httpParams = httpParams.set('filter', JSON.stringify(filters));
        }

        return this.taskDataService.getDashboardData(httpParams).pipe(
          map((response) => ({
            dashboardArgs: params,
            response: response,
          }))
        );
      } else {
        return of({
          dashboardArgs: params,
          response: [],
        });
      }
    }),
    map((result) => {
      if (result && result.response) {
        return {
          dashboardArgs: result.dashboardArgs,
          data: result.response[1],
        };
      } else {
        return {
          dashboardArgs: result.dashboardArgs,
          data: [],
        };
      }
    })
  );

  private getFiltersFromDashboardArgs(params: IDashboardArgs) {
    let filters: any[] = [];

    // Handle scope filters
    if (params.scope === ScopeOptions.MyTeamTask) {
      filters.push(['myTeams', '=', true]);
    }
    else if (params.scope === ScopeOptions.MyTask) {
      filters.push(['assignedUserName', '=', localStorage.getItem('loggedInUser')]);
    }

    // Handle status filter based on showOpen and showRejected flags
    const statusFilter = this.generateStatusFilter(params.showOpen, params.showRejected);
    if (statusFilter) {
      if (filters.length > 0) {
        filters.push('and');
      }
      filters.push(statusFilter);
    }

    // Handle endDate filter
    if (params.endDate !== null) {
      if (filters.length > 0) {
        filters.push('and');
      }
      filters.push(['dueDate', '<=', params.endDate]);
    }

    return filters;
  }

  private generateStatusFilter(showOpen: boolean, showRejected: boolean): any[] | null {
    let statusConditions: any[] = [];

    if (showOpen && !showRejected) {
      // showOpen = true, showRejected = false
      statusConditions = [
        ['status', '=', 'open'],
        'or',
        ['status', '=', 'pending'],
        'or',
        ['status', '=', 'pending client'],
        'or',
        ['status', '=', 'client hold']
      ];
    } else if (!showOpen && !showRejected) {
      // showOpen = false, showRejected = false
      statusConditions = [
        ['status', '=', 'pending'],
        'or',
        ['status', '=', 'open'],
        'or',
        ['status', '=', 'pending client'],
        'or',
        ['status', '=', 'client hold'],
        'or',
        ['status', '=', 'completed'],
        'or',
        ['status', '=', 'cancelled'],
        'or',
        ['status', '=', 'closed']
      ];
    } else if (!showOpen && showRejected) {
      // showOpen = false, showRejected = true
      statusConditions = [
        ['status', '=', 'open'],
        'or',
        ['status', '=', 'completed'],
        'or',
        ['status', '=', 'cancelled'],
        'or',
        ['status', '=', 'pending client'],
        'or',
        ['status', '=', 'client hold'],
        'or',
        ['status', '=', 'closed'],
        'or',
        ['status', '=', 'rejected'],
        'or',
        ['status', '=', 'pending']
      ];
    } else if (showOpen && showRejected) {
      // showOpen = true, showRejected = true
      statusConditions = [
        ['status', '=', 'rejected'],
        'or',
        ['status', '=', 'pending'],
        'or',
        ['status', '=', 'pending client'],
        'or',
        ['status', '=', 'client hold'],
        'or',
        ['status', '=', 'open']
      ];
    }

    return statusConditions.length > 0 ? statusConditions : null;
  }

  selectedDashboardArgsChanged(
    args: IDashboardArgs
  ) {
    this.dashboardCriteriaSelectedSubject.next(args);
  }

  dashboardTables$ = this.dashboardData$.pipe(
    map((result) => {

      const subTasksByPeople = DashboardTaskDataMapper.generateTaskSummaryTable('Person', 'Subtasks by People', result.data.peopleSummaryData, result.dashboardArgs);
      const subtasksByType = DashboardTaskDataMapper.generateTaskSummaryTable('Type', 'Subtasks by Type', result.data.subTypeSummaryData, result.dashboardArgs);
      const tasksByClient = DashboardTaskDataMapper.generateTaskSummaryTable('Client', 'Tasks by Client', result.data.clientTaskSummaryData, result.dashboardArgs);
      const subTasksByClient = DashboardTaskDataMapper.generateTaskSummaryTable('Client', 'Subtasks by Client', result.data.clientSubTaskSummaryData, result.dashboardArgs);
      const tasksByType = DashboardTaskDataMapper.generateTaskSummaryTable('Type', 'Tasks by Type', result.data.typeSummaryData, result.dashboardArgs);

      const tables = [
        tasksByClient,
        subTasksByClient,
        subTasksByPeople,
        tasksByType,
        subtasksByType
      ];

        return tables;

      }

  ));
}
